import {
  List,
  TextField,
  FunctionField,
  TextInput,
  NumberField,
  TopToolbar,
  SelectColumnsButton,
  DatagridConfigurable,
  Button,
  SelectInput,
  DateField,
  DateInput,
  ListActionsProps,
  FilterButton,
  FilterForm,
  NumberInput,
} from "react-admin";
import { Stack } from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import Chip from "@mui/material/Chip";
import { Link } from "react-router-dom";
import { brandChoises } from "../interfaces";
import {
  createdFromCIChoises,
  createdFromCILabels,
  createdFromCIMap,
} from "../interfaces/ClientInventory.interface";
import { formatDateToFrenshFormat } from "../utils/formatDateToFrenshFormat";

export const hasInprogressOrderChoises = [
  { id: "true", name: "Oui" },
  { id: "false", name: "Non" },
];

export const ClientFilter = [
  <TextInput label="Code interne" source="internalCode" alwaysOn />,
  <TextInput label="Nom du client" source="name" />,
  <SelectInput source="brand" choices={brandChoises} label="Marque" alwaysOn />,
  <SelectInput
    source="hasInprogressOrder"
    label="BDC en cours"
    choices={hasInprogressOrderChoises}
    alwaysOn
  />,
  <SelectInput
    source="latestInventorycreatedFrom"
    label="Inv. ajouté par"
    choices={createdFromCIChoises}
    alwaysOn
  />,
  <DateInput source="startDateProvisional" label="D. début (Prévisionnelle)" />,
  <DateInput source="endDateProvisional" label="D. fin (Prévisionnelle)" />,
  <NumberInput source="airbagsCount" label="Nb airbag sup" alwaysOn />,
  <NumberInput source="fullBinsCount" label="Caisses pleines sup" />,
  <NumberInput source="emptyBinsCount" label="Caisses vides sup" />,
  <DateInput source="startDateInventory" label="D. début (Inv.)" />,
  <DateInput source="endDateInventory" label="D. fin (Inv.)" />,
  <DateInput
    source="startDateLastOrderDate"
    alwaysOn
    label="D. début (dernier BDC)"
  />,
  <DateInput
    source="endDateLastOrderDate"
    alwaysOn
    label="D. fin (dernier BDC)"
  />,
  <DateInput source="startDateCreatedAt" label="D. début (création clt)" />,
  <DateInput source="endDateLastCreatedAt" label="D. fin (création clt)" />,
  <TextInput label="Adresse" source="address" />,
  <TextInput label="Code postal" source="zipCode" />,
  <TextInput label="Ville" source="city" />,
];

const ListActions: React.FC<ListActionsProps> = (props) => (
  <TopToolbar>
    <Stack direction="row" sx={{ justifyContent: "space-between" }}>
      <FilterForm filters={ClientFilter} />
      <div>
        <FilterButton filters={ClientFilter} />
        <SelectColumnsButton />
        <Button component={Link} to="/orders/create">
          + BDC
        </Button>
      </div>
    </Stack>
  </TopToolbar>
);

export const Dashboard: React.FC = (props) => (
  <>
    <List
      {...props}
      resource="clients"
      actions={<ListActions />}
      title="Dashboard"
      sort={{ field: "createdAt", order: "DESC" }}
    >
      <DatagridConfigurable rowClick={false} bulkActionButtons={false}>
        <TextField source="internalCode" label="Code interne" />
        <TextField source="name" label="Nom" />
        <TextField textAlign="center" source="brand" label="Marque" />
        <NumberField
          source="airbagsCount"
          label="Nb airbag en vrac"
          textAlign="center"
        />
        <NumberField
          source="fullBinsCount"
          label="Caisses pleines"
          textAlign="center"
        />
        <NumberField
          source="emptyBinsCount"
          label="Caisses vides"
          textAlign="center"
        />
        <FunctionField
          source="lastDateInventory"
          textAlign="center"
          label="D. MAJ inv"
          render={(record: any) =>
            record.lastDateInventory
              ? formatDateToFrenshFormat(record.lastDateInventory, true)
              : ""
          }
        />
        <FunctionField
          label="BDC en cours"
          textAlign="center"
          render={(record) =>
            record.hasInprogressOrder ? (
              <Chip label="Oui" color="success" size="small" />
            ) : (
              <Chip label="Non" color="default" size="small" />
            )
          }
        />
        <FunctionField
          label="Inv. MAJ après BDC"
          textAlign="center"
          render={(record) =>
            record.inventoryUpdatedAfterOrder ? (
              <Chip label="À vérifier" color="warning" size="small" />
            ) : null
          }
        />
        <DateField
          source="lastProvisionalDate"
          textAlign="center"
          label="D. prévisionnelle"
          locales="fr-FR"
          options={{
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }}
        />
        <FunctionField
          source="latestExecutionDate"
          textAlign="center"
          label="D. d'execution"
          render={(record: any) =>
            record.latestExecutionDate
              ? formatDateToFrenshFormat(record.latestExecutionDate, true)
              : ""
          }
        />
        <DateField
          source="latestOderDate"
          textAlign="center"
          label="D. dernier BDC"
          locales="fr-FR"
          options={{
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }}
        />

        <FunctionField
          source="latestInventorycreatedFrom"
          textAlign="center"
          label="Inv. ajouté par"
          render={(record) => {
            let label =
              createdFromCILabels[record.latestInventorycreatedFrom] ||
              record.latestInventorycreatedFrom;
            const color =
              createdFromCIMap[record.latestInventorycreatedFrom] || "default";
            label = label || "N/A";
            return <Chip label={label} color={color} size="small" />;
          }}
        />
        <TextField source="address" textAlign="center" label="Adresse" />
        <TextField source="zipCode" textAlign="center" label="Code postal" />
        <TextField source="city" textAlign="center" label="Ville" />
        <FunctionField
          source="createdAt"
          textAlign="center"
          label="D. de creation clt"
          render={(record: any) =>
            record.createdAt
              ? formatDateToFrenshFormat(record.createdAt, true)
              : ""
          }
        />
        <FunctionField
          label="Inv."
          textAlign="center"
          render={(record) => (
            <Button
              component={Link}
              label="Voir"
              to={`/client-inventory/details/${record.id}`}
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <PreviewIcon />
            </Button>
          )}
        />
      </DatagridConfigurable>
    </List>
  </>
);
